import React from 'react'
import { TouchableOpacity } from 'react-native'
import { Feather } from '../../../components/icon/vectorIcons'

import { Flex } from '../../../components/FlexBox'
import { H5, Text } from '../../../components/common/Text'
import { Switch } from '../../../components/common/Switch'
import DraggableList, {
  DragHandlePlaceholder
} from '../../../components/common/DraggableList'
import useTranslation from '../../../hooks/useTranslation'
import { HERO_SLOT_COUNT } from '../constants'
import { IContentFeedBoost } from '../queries/getContentFeeds'
import HeroSlot from './HeroSlot'
import PublishBar from './PublishBar'
import { TableCol, TableContainer, TableRow } from './Table'

interface HeroFeedPanelProps {
  boosts: IContentFeedBoost[]
  loading: boolean
  error?: unknown
  /** Staged value, not feed.enabled -- it may differ until published. */
  enabled: boolean
  changeCount: number
  publishing: boolean
  /** Set when the band is not full; blocks Publish and explains why. */
  publishBlockedReason?: string | null
  onPublish: () => void
  onDiscard: () => void
  onToggleEnabled: () => void
  onConfigure: () => void
  onReorder: (next: IContentFeedBoost[]) => void
  onRemove: (boost: IContentFeedBoost) => void
  /** Position is 1-based; an index past the end fills the next empty slot. */
  onChoose: (index: number) => void
}

/**
 * The featured band, editable in place. Filled slots are draggable; empty ones
 * are not, and carry a disabled handle so the column still lines up and the
 * unavailability is visible rather than implied by a gap.
 */
export const HeroFeedPanel = ({
  boosts,
  loading,
  error,
  enabled,
  changeCount,
  publishing,
  publishBlockedReason,
  onPublish,
  onDiscard,
  onToggleEnabled,
  onConfigure,
  onReorder,
  onRemove,
  onChoose
}: HeroFeedPanelProps) => {
  const { t } = useTranslation()

  const emptySlots = Array.from(
    { length: Math.max(0, HERO_SLOT_COUNT - boosts.length) },
    (_, i) => boosts.length + i
  )

  return (
    <Flex mx={4} mb={3} flexDirection="row">
      <TableContainer>
        <Flex flexDirection="row" alignItems="center" p={2}>
          <Flex flex={1}>
            <H5 styles={{ fontWeight: '700' }}>
              {t('contentCuration:hero:title')}
            </H5>
            <Text styles={{ fontSize: 12 }}>
              {t('contentCuration:hero:description')}
            </Text>
          </Flex>
          <Flex flexDirection="row" alignItems="center">
            <Text styles={{ fontSize: 12, marginRight: 12 }}>
              {`${boosts.length}/${HERO_SLOT_COUNT}`}
            </Text>
            <Text styles={{ fontSize: 12, marginRight: 8 }}>
              {enabled
                ? t('contentCuration:list:enabled')
                : t('contentCuration:list:disabled')}
            </Text>
            <Switch value={enabled} onValueChange={onToggleEnabled} />
            {/* A cog opening the settings modal in place. This used to be a
                Configure button that navigated to the feed's own page, which
                for the featured feed was a near-empty duplicate of this
                section. */}
            <TouchableOpacity
              onPress={onConfigure}
              accessibilityLabel={t('contentCuration:settings:title')}
              style={{ padding: 8, marginLeft: 4 }}
            >
              <Feather name="settings" size={20} color="#21201B" />
            </TouchableOpacity>
          </Flex>
        </Flex>

        {loading && (
          <TableRow>
            <TableCol>
              <Text>{t('common:loading')}</Text>
            </TableCol>
          </TableRow>
        )}
        {!loading && !!error && (
          <TableRow>
            <TableCol>
              <Text>{t('contentCuration:hero:error')}</Text>
            </TableCol>
          </TableRow>
        )}

        {!loading && !error && (
          <>
            <DraggableList
              items={boosts}
              keyExtractor={boost => `${boost.itemType}:${boost.itemId}`}
              onReorder={onReorder}
              renderItem={(boost, index) => (
                <HeroSlot
                  position={index + 1}
                  boost={boost}
                  onChoose={() => onChoose(index)}
                  onRemove={() => onRemove(boost)}
                />
              )}
            />
            {emptySlots.map(index => (
              <Flex key={index} flexDirection="row">
                {/* A disabled handle, not a blank spacer: an empty slot cannot
                    be reordered, but showing the control greyed out explains
                    that better than a gap where every other row has one. */}
                <DragHandlePlaceholder />
                <Flex flex={1}>
                  <HeroSlot
                    position={index + 1}
                    onChoose={() => onChoose(index)}
                    onRemove={() => {}}
                  />
                </Flex>
              </Flex>
            ))}
          </>
        )}

        <PublishBar
          changeCount={changeCount}
          publishing={publishing}
          blockedReason={publishBlockedReason}
          onPublish={onPublish}
          onDiscard={onDiscard}
        />
      </TableContainer>
    </Flex>
  )
}

export default HeroFeedPanel
